import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
import useGoogleAuthFlow from '../hooks/useGoogleAuthFlow';
import apiClient from '../api/axios';

// Simple email regex — covers the vast majority of valid addresses
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function Login() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { t } = useTranslation(['login', 'common']);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const { handleGoogleCredential, googleError, googleLoading } = useGoogleAuthFlow();

  const validate = () => {
    const errs = { email: '', password: '' };
    if (!email.trim()) {
      errs.email = t('validation.emailRequired');
    } else if (!EMAIL_REGEX.test(email.trim())) {
      errs.email = t('validation.emailInvalid');
    }
    if (!password) {
      errs.password = t('validation.passwordRequired');
    } else if (password.length < 6) {
      errs.password = t('validation.passwordTooShort');
    }
    setFieldErrors(errs);
    return !errs.email && !errs.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await apiClient.post('/login', { username: email.trim(), password });
      if (res.data?.token) {
        auth.login(res.data.token);
      }
      navigate('/dashboard');
    } catch (err) {
      if (err.response) {
        if (err.response.status === 401 || err.response.status === 403) {
          setError(t('error.invalidCredentials'));
        } else {
          setError(t('error.serverError', { status: err.response.status }));
        }
      } else {
        setError(t('error.networkError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 px-4 py-10">
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-extrabold tracking-tight">
            <span style={{ color: '#085041' }}>Bento</span>
            <span className="text-orange-600">Ping</span>
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {t('common:tagline')}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-orange-100/60 border border-orange-100 p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-1">
                {t('welcomeBack')}
              </h2>
              <p className="text-sm text-gray-500">{t('signInSubtitle')}</p>
            </div>
            <LanguageSwitcher className="text-sm border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-600 bg-gray-50 hover:border-orange-300 focus:outline-none focus:ring-2 focus:ring-orange-200 cursor-pointer" />
          </div>

          {(error || googleError) && (
            <div
              role="alert"
              className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4 mt-0.5 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
                />
              </svg>
              <span>{error || googleError}</span>
            </div>
          )}

          <div className="mb-5 flex justify-center">
            <GoogleSignInButton onCredential={handleGoogleCredential} disabled={googleLoading} />
          </div>

          <div className="mb-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs text-gray-400">{t('orContinueWithEmail')}</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1.5"
            >
              {t('emailLabel')}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (fieldErrors.email) {
                  setFieldErrors({ ...fieldErrors, email: '' });
                }
              }}
              placeholder={t('emailPlaceholder')}
              className={`w-full px-3.5 py-2.5 rounded-lg border bg-white text-gray-900 placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-orange-200 ${
                fieldErrors.email
                  ? 'border-red-400 focus:border-red-500'
                  : 'border-gray-300 focus:border-orange-500'
              }`}
              disabled={loading}
            />
            {fieldErrors.email && (
              <p className="mt-1.5 text-xs text-red-600">{fieldErrors.email}</p>
            )}

            {/* Password */}
            <div className="mt-4 flex items-center justify-between mb-1.5">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700"
              >
                {t('passwordLabel')}
              </label>
              <button
                type="button"
                onClick={() => {
                  // Placeholder — wire up later
                  console.log('Forgot password clicked');
                }}
                className="text-xs font-medium text-orange-600 hover:text-orange-700 hover:underline"
              >
                {t('forgotPassword')}
              </button>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password) {
                    setFieldErrors({ ...fieldErrors, password: '' });
                  }
                }}
                placeholder={t('passwordPlaceholder')}
                className={`w-full px-3.5 py-2.5 pr-11 rounded-lg border bg-white text-gray-900 placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-orange-200 ${
                  fieldErrors.password
                    ? 'border-red-400 focus:border-red-500'
                    : 'border-gray-300 focus:border-orange-500'
                }`}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                tabIndex={-1}
              >
                {showPassword ? (
                  // Eye-off icon
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 3l18 18M10.58 10.58a2 2 0 0 0 2.83 2.83M9.88 4.61A10.94 10.94 0 0 1 12 4.5c5 0 9.27 3.11 11 7.5a11.72 11.72 0 0 1-3.17 4.39M6.61 6.61A11.74 11.74 0 0 0 1 12c1.73 4.39 6 7.5 11 7.5a10.94 10.94 0 0 0 5.39-1.39"
                    />
                  </svg>
                ) : (
                  // Eye icon
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M1 12s4-7.5 11-7.5S23 12 23 12s-4 7.5-11 7.5S1 12 1 12Z"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.password && (
              <p className="mt-1.5 text-xs text-red-600">
                {fieldErrors.password}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg
                    className="w-4 h-4 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
                    />
                  </svg>
                  {t('signingIn')}
                </>
              ) : (
                t('signIn')
              )}
            </button>
          </form>

          {/* Get started link */}
          <p className="mt-6 text-center text-sm text-gray-600">
            {t('newUser')}{' '}
            <button
              type="button"
              onClick={() => navigate('/pricing')}
              className="font-medium text-orange-600 hover:text-orange-700 hover:underline"
            >
              {t('getStarted')}
            </button>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 mt-6">
          {t('common:footer', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
}

export default Login;
